// must be imported before React and React DOM
import '@fontsource-variable/roboto'
// Initialize deployment manager for chunk loading error recovery
// This must be imported after vite define variables are available
import './common/utils/deploymentManager'

import { StrictMode, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import TagManager from 'react-gtm-module'
import { I18nextProvider } from 'react-i18next'
import { Provider } from 'react-redux'
import { IntercomProvider } from 'react-use-intercom'
import { AppInsightsContext, AppInsightsErrorBoundary } from '@microsoft/applicationinsights-react-js'
import { PostHogProvider } from '@posthog/react'
import { SnackbarProvider } from 'notistack'
import posthog from 'posthog-js'
import { PersistGate } from 'redux-persist/integration/react'

import { AuthProvider } from '@/common/components/AuthProvider'

import { persistor, store } from './app/store'
import { ErrorPageContainer } from './common/components'
import { LoadingProvider } from './common/contexts/LoadingContext'
import { browserHistory, reactPlugin } from './common/utils'
import App from './App'
import i18n from './i18n'

// Note: Favicon and title are now set dynamically by useFaviconUpdater() hook in Layout components
// This allows branding to react to Split.io feature flags after they load

// Google Tag Manager

const tagManagerArgs = {
    gtmId: import.meta.env.VITE_GTM_ID,
}

TagManager.initialize(tagManagerArgs)

const rootElement = document.getElementById('root')

const onError = () => (
    <ErrorPageContainer
        linkHref="/"
        linkText="Back to your dashboard"
        subtitle="It's not you, it's us, please try that again, or contact support for help."
        title="Something went wrong"
    />
)

posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: '2025-05-24',
    capture_exceptions: true,
    debug: import.meta.env.MODE === 'development',
})

// Render the app - MSAL initialization is lazy-loaded via AuthProvider
const renderApp = () => {
    createRoot(rootElement).render(
        <StrictMode>
            <PostHogProvider client={posthog}>
                <Suspense>
                    <Provider store={store}>
                        <PersistGate
                            loading={null}
                            persistor={persistor}
                        >
                            <AppInsightsErrorBoundary
                                appInsights={reactPlugin}
                                onError={onError}
                            >
                                <AppInsightsContext.Provider value={reactPlugin}>
                                    <I18nextProvider i18n={i18n}>
                                        <AuthProvider>
                                            <IntercomProvider appId={import.meta.env.VITE_INTERCOM_APP_ID}>
                                                <SnackbarProvider
                                                    anchorOrigin={{
                                                        vertical: 'bottom',
                                                        horizontal: 'left',
                                                    }}
                                                    autoHideDuration={2500}
                                                    maxSnack={3}
                                                    preventDuplicate
                                                >
                                                    <LoadingProvider>
                                                        <App />
                                                    </LoadingProvider>
                                                </SnackbarProvider>
                                            </IntercomProvider>
                                        </AuthProvider>
                                    </I18nextProvider>
                                </AppInsightsContext.Provider>
                            </AppInsightsErrorBoundary>
                        </PersistGate>
                    </Provider>
                </Suspense>
            </PostHogProvider>
        </StrictMode>
    )
}

// Start the application
renderApp()

export { browserHistory as history }
